import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, TrendingUp, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n, useT } from "@/i18n/I18nContext";

const BG_IMG = "https://images.pexels.com/photos/12732218/pexels-photo-12732218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ACCIDENT_TYPES = [
  ["Rear-end collision", "intake.type.rear_end"],
  ["Head-on collision", "intake.type.head_on"],
  ["T-bone / side-impact", "intake.type.tbone"],
  ["Hit and run", "intake.type.hit_run"],
  ["Pedestrian struck", "intake.type.pedestrian"],
  ["Motorcycle accident", "intake.type.motorcycle"],
  ["Rideshare", "intake.type.rideshare"],
  ["Truck / 18-wheeler", "intake.type.truck"],
  ["Other", "intake.type.other"],
];

const FAULT_OPTIONS = [
  ["Other driver", "intake.fault.other"],
  ["Me", "intake.fault.me"],
  ["Shared fault", "intake.fault.shared"],
  ["Not sure", "intake.fault.unsure"],
];

export default function Estimator() {
  const t = useT();
  const { lang } = useI18n();
  const [form, setForm] = useState({
    state: "NY",
    accident_type: "",
    injuries: "",
    medical_treatment: false,
    at_fault: "Other driver",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/estimator", { ...form, language: lang });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const usd = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : n);

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${BG_IMG})` }} aria-hidden />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FDFBF7]/95 via-[#FDFBF7]/95 to-[#FDFBF7]" aria-hidden />
        <div className="mx-auto max-w-6xl px-6 py-16 lg:px-12 lg:py-20">
          <span className="label-cap">{t("est.label")}</span>
          <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
            {t("est.title")}
          </h1>
          <p className="mt-3 max-w-2xl text-[#5C6B68]">
            {t("est.body")} <em>{t("est.not_advice")}</em>
          </p>

          <div className="mt-10 grid gap-8 lg:grid-cols-5">
            <form onSubmit={submit} className="card-soft p-6 sm:p-8 lg:col-span-3" data-testid="estimator-form">
              <div className="grid gap-5">
                <div>
                  <label className="label-cap">{t("intake.state_label")}</label>
                  <div className="mt-2 flex gap-3">
                    {["NY", "NJ"].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => set("state", s)}
                        className={`flex-1 rounded-lg border-2 py-3 font-display font-bold transition ${form.state === s ? "border-[#11322A] bg-[#11322A] text-white" : "border-[#E0E4E2] text-[#0B1317]"}`}
                        data-testid={`est-state-${s}`}
                      >
                        {s === "NY" ? t("intake.state_ny") : t("intake.state_nj")}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label-cap">{t("intake.type_label")}</label>
                  <select className="input-field mt-2" value={form.accident_type} onChange={(e) => set("accident_type", e.target.value)} required data-testid="est-type">
                    <option value="">{t("intake.select")}</option>
                    {ACCIDENT_TYPES.map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-cap">{t("est.injuries_label")}</label>
                  <textarea rows={3} className="input-field mt-2" placeholder={t("est.injuries_placeholder")} value={form.injuries} onChange={(e) => set("injuries", e.target.value)} required data-testid="est-injuries" />
                </div>
                <div>
                  <label className="label-cap">{t("est.fault_label")}</label>
                  <select className="input-field mt-2" value={form.at_fault} onChange={(e) => set("at_fault", e.target.value)} data-testid="est-fault">
                    {FAULT_OPTIONS.map(([value, key]) => (
                      <option key={value} value={value}>{t(key)}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-3 text-sm font-semibold text-[#0B1317]">
                  <input type="checkbox" checked={form.medical_treatment} onChange={(e) => set("medical_treatment", e.target.checked)} data-testid="est-treatment" />
                  {t("est.treatment_label")}
                </label>
                <div>
                  <label className="label-cap">Additional details (optional)</label>
                  <textarea rows={3} className="input-field mt-2" placeholder="Lost wages, ongoing therapy, vehicle damage, etc." value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="est-description" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50" data-testid="est-submit">
                  <Calculator className="h-4 w-4" />
                  {loading ? t("est.analyzing") : t("est.submit")}
                </button>
              </div>
            </form>

            <div className="lg:col-span-2">
              <div className="card-soft p-6 sm:p-8" data-testid="estimator-result">
                {!result && !loading && (
                  <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                    <div className="grid h-12 w-12 place-items-center rounded-full bg-[#FDFBF7] border border-[#E0E4E2]">
                      <TrendingUp className="h-5 w-5 text-[#11322A]" />
                    </div>
                    <p className="mt-4 max-w-xs text-sm text-[#5C6B68]">
                      {t("est.placeholder.body")}
                    </p>
                  </div>
                )}
                {loading && (
                  <div className="flex h-full min-h-[300px] items-center justify-center">
                    <div className="text-sm text-[#5C6B68]">{t("est.placeholder.loading")}</div>
                  </div>
                )}
                {result && (
                  <div>
                    <div className="label-cap">{t("est.range_label")}</div>
                    <div className="mt-2 font-display text-3xl font-black tracking-tight text-[#11322A] sm:text-4xl">
                      ${usd(result.estimated_low_usd)} – ${usd(result.estimated_high_usd)}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E0E4E2] bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-[#11322A]">
                      {t("est.strength_label")} {result.case_strength}
                    </div>
                    {Array.isArray(result.key_factors) && (
                      <ul className="mt-6 space-y-2 text-sm text-[#0B1317]">
                        {result.key_factors.map((f, i) => (
                          <li key={`kf-${i}`} className="flex gap-2"><span className="text-[#E85D04]">•</span><span>{f}</span></li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-6 flex items-start gap-2 rounded-lg border border-[#E0E4E2] bg-[#FDFBF7] p-3 text-xs text-[#5C6B68]">
                      <AlertCircle className="h-4 w-4 shrink-0 text-[#D9381E]" />
                      <span>{result.disclaimer || t("est.disclaimer_fallback")}</span>
                    </div>
                    <Link to="/intake" className="btn-primary mt-6 w-full" data-testid="est-cta">
                      {t("est.cta_match")}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
